import { useState } from "react";
import DOMPurify from "dompurify";
import { ChevronDown } from "lucide-react";

interface ContestDescriptionProps {
    value: string | null;
    className?: string;
}

/**
 * Renders contest description with proper spacing for both HTML and plain text.
 * - HTML content: Sanitized and rendered with prose typography classes
 * - Plain text: Rendered with whitespace-pre-wrap to preserve line breaks
 */
export function ContestDescription({ value, className = "" }: ContestDescriptionProps) {
    if (!value) return null;

    const [expanded, setExpanded] = useState(false);

    // Threshold for truncation (approx. 400 chars)
    const shouldTruncate = value.length > 400;
    const isTruncated = shouldTruncate && !expanded;

    // Simple heuristic: if it contains HTML tags, treat as HTML
    const looksLikeHtml = /<[^>]+>/.test(value);

    const content = looksLikeHtml ? (
        <div
            className={`prose prose-lg prose-slate dark:prose-invert prose-p:my-3 prose-p:leading-relaxed prose-ul:my-3 prose-ol:my-3 prose-li:my-1 [&_p:empty]:min-h-[1.5em] [&_p:has(>br:only-child)]:min-h-[1.5em] ${className} ${isTruncated ? "line-clamp-4 overflow-hidden" : ""}`}
            dangerouslySetInnerHTML={{
                __html: (() => {
                    const clean = DOMPurify.sanitize(value, {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'b', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                        ALLOWED_ATTR: ['href', 'target', 'rel']
                    });
                    // Post-process: enforce noopener noreferrer on all anchor tags
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(clean, 'text/html');
                    doc.querySelectorAll('a').forEach(anchor => {
                        anchor.setAttribute('rel', 'noopener noreferrer');
                        if (!anchor.getAttribute('href')?.startsWith('#')) {
                            anchor.setAttribute('target', '_blank');
                        }
                    });
                    return doc.body.innerHTML;
                })()
            }}
        />
    ) : (
        <p className={`text-lg leading-relaxed whitespace-pre-wrap ${className} ${isTruncated ? "line-clamp-4 overflow-hidden" : ""}`}>
            {value}
        </p>
    );

    if (!shouldTruncate) {
        return content;
    }

    return (
        <div>
            <div className="relative">
                {content}

                {isTruncated && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--contest-bg,#f8fafc)] to-transparent pointer-events-none" />
                )}
            </div>

            <div className="flex justify-center w-full mt-2">
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 cursor-pointer transition-colors"
                    aria-expanded={expanded}
                >
                    {expanded ? "Show less" : "Read more"}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
                </button>
            </div>
        </div>
    );
}
